import React, { useState, useContext, useRef, useEffect } from 'react'
import { AppContext } from '../../index'
import * as actions from '../store/actions'
import { useHistory } from 'react-router-dom'
import './NavSearch.css'
import { debounce } from 'lodash'
import { NativeEvent } from '../../../modules/types'
import slugify from 'react-slugify'
import { SubscriptionInfo, SubscriptionSearchResult, AccountInfo } from '../store/store'
import { Partner } from '../../partner/store/store'
import { RequestState } from '../../../modules/types'
import { resetSearch } from '../store/actions'
import { useTranslation } from 'react-i18next'

function NavSearch() {
  const { state, execute } = useContext(AppContext)
  const [popupOpen, setPopupOpen] = useState(false)
  const [searchText, setSearchText] = useState('')
  const subscriptionSearchResults = state.search.subscriptions.results
  const accountSearchResults = state.search.accounts.results
  const accountPartnerSearchResults = state.search.accounts.otherPartnerResults
  const history = useHistory()
  const partnerId  = state.partner.currentPartner ? state.partner.currentPartner.id : undefined
  const partners = state.auth.partners
  const partnerIds = partners.map((p: Partner) => p.id)
  const subscriptionsLoading = state.search.subscriptions.requestState === RequestState.Loading
  const accountsLoading = state.search.accounts.requestState === RequestState.Loading
  const includeDeactivated = state.search.includeDeactivated
  const { t } = useTranslation(['navigation','global'])

  // the reason we need a ref here is the component closure captures the old value
  const searchRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    window.addEventListener('click', (e: NativeEvent) => {
      if (e.srcElement.id !== 'search') {
        setPopupOpen(false)
      }
    })
  }, [])

  useEffect(() => {
    // clear search on partner change
    if (searchRef && searchRef.current) {
      searchRef.current.value = ''
    }
  }, [partnerId])

  useEffect(() => {
    if (partnerId && searchText.length >= 2) {
      execute(actions.getSubscriptionSearchResults(partnerIds, searchText, includeDeactivated))
      execute(actions.getAccountSearchResults(partnerId, partnerIds.filter((pid: string) => pid !== partnerId), searchText, includeDeactivated))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchText, includeDeactivated])

  function updateSearchText() {
    if (searchRef && searchRef.current) {
      if (searchRef.current.value.length < 2) {
        setPopupOpen(false)
        execute(actions.resetSearch())
      } else {
        setPopupOpen(true)
        setSearchText(searchRef.current.value)
      }
    }
  }

  function toggleDeactivated() {
    execute(actions.toggleDeactivated(!includeDeactivated))
  }

  const debouncedUpdateSearchText = debounce(updateSearchText, 200)

  function onSubscriptionClick(id: string) {
    history.push(`/partner/${partnerId}/subscriptions?subscription=${id}`)
  }

  function onAccountClick(id: string) {
    history.push(`/partner/${partnerId}/subscriptions?account=${id}`)
  }

  function getSearchParams() {
    if (searchRef && searchRef.current) {
      if (includeDeactivated) {
        return new URLSearchParams({ search: `${searchRef.current.value.trim()}`, deactivated: 'true' })
      } else {
        return new URLSearchParams({ search: `${searchRef.current.value.trim()}` })
      }
    }
  }

  function onSubscriptionViewAllClick() {
    if (searchRef && searchRef.current) {
      history.push(`/partner/${partnerId}/subscriptions?${getSearchParams()}`)
    }
  }

  function onAccountViewAllClick() {
    if (searchRef && searchRef.current) {
      history.push(`/partner/${partnerId}/accounts?${getSearchParams()}`)
    }
  }

  function onSearchFocus() {
    if (searchRef && searchRef.current && searchRef.current.value.length >= 2) {
      setPopupOpen(true)
    } else {
      execute(resetSearch())
    }
  }

  function onClear() {
    if (searchRef && searchRef.current) {
      searchRef.current.value = ''
    }
  }

  function subscriptionSearchResultsReturned() {
    return subscriptionSearchResults.some((result: SubscriptionSearchResult) => result.results.length > 0)
  }

  const currentPartnerSearchItem = subscriptionSearchResults
    ? subscriptionSearchResults.find((p: SubscriptionSearchResult) => p.partnerId === partnerId)
    : null

  const currentPartnerSearchResults = currentPartnerSearchItem
    ? currentPartnerSearchItem.results
    : null

  function onPartnerSubscriptionSearch(id: string) {
    history.push(`/partner/${id}/subscriptions?${getSearchParams()}`)
  }

  function onPartnerAccountSearch(id: string) {
    history.push(`/partner/${id}/accounts?${getSearchParams()}`)
  }

  function renderSubscriptionSearchLinks() {
    const searches = []

    for (const result of subscriptionSearchResults) {
      if (result.partnerId !== partnerId && result.results.length > 0) {
        searches.push(
          <div
            id={result.partnerId}
            key={result.partnerId}
            className="partner-subscriptions"
            onClick={() => onPartnerSubscriptionSearch(result.partnerId)}>
            <span>{ t('results_for')} &quot;{partners.find((p: Partner) => p.id === result.partnerId)?.name}&quot;</span>
            <i className="fas fa-chevron-right link-chevron"></i>
          </div>
        )
      }
    }

    return searches
  }

  function renderAccountSearchLinks() {
    const searches = []

    for (const result of accountPartnerSearchResults) {
      searches.push(
        <div
          id={result.id}
          key={result.id}
          className="partner-accounts"
          onClick={() => onPartnerAccountSearch(result.id)}>
          <span>{ t('results_for')} &quot;{partners.find((p: Partner) => p.id === result.id)?.name}&quot;</span>
          <i className="fas fa-chevron-right link-chevron"></i>
        </div>
      )
    }

    return searches
  }

  return (
    <>
        <div>
          <form autoComplete="off" onSubmit={e => { e.preventDefault() }}>
            <input
              id="search"
              ref={searchRef}
              onFocus={onSearchFocus}
              onChange={debouncedUpdateSearchText}
              type="text"
              placeholder={ t('search') + '...'}
            />
          </form>
          {searchRef && searchRef.current && searchRef.current.value.length > 0 && (
            <div id="search-buttons">
              <i id="clear-search" className="fas fa-times" onClick={onClear}></i>
            </div>
          )}
        </div>

      {popupOpen && (
        <div className="search-container">
          <div id="search-toggle-inactive" onClick={e => { e.stopPropagation() }}>
            <input type="checkbox"
                   defaultChecked={includeDeactivated}
                   onChange={() => { toggleDeactivated() }}/> { t('include_deactivated')}
          </div>
          <div className="title-item">
            <div>
              <span className="title-item-text">{ t('subscriptions')}</span>
              {currentPartnerSearchResults && currentPartnerSearchResults.length > 0 && (
                <span id="view-all-subscriptions" className="link" onClick={() => onSubscriptionViewAllClick()}>
                  <span>{ t('view_all')}</span>
                  <i className="fas fa-chevron-right link-chevron"></i>
                </span>
              )}
            </div>

            {subscriptionsLoading && <i className="fas fa-spinner fa-spin"></i>}
          </div>

          {subscriptionsLoading && !subscriptionSearchResultsReturned() && (
            <div className="message-item">{ t('global:please_wait')}</div>
          )}

          {!subscriptionsLoading && !subscriptionSearchResultsReturned() && (
            <div id="subscriptions-message" className="message-item">
              No matches.
            </div>
          )}

          <div id="subscription-search-results">
            {currentPartnerSearchResults && currentPartnerSearchResults.map((result: SubscriptionInfo) => (
              <div
                id={slugify(result.name)}
                key={result.id}
                className={'search-item ' + (result.active ? '' : 'inactive')}
                onClick={() => onSubscriptionClick(result.id)}>
                {result.name}
              </div>
            ))}

            {renderSubscriptionSearchLinks()}
          </div>

          <div className="title-item">
            <div>
              <strong className="title-item-text">{ t('accounts')}</strong>
              {accountSearchResults && accountSearchResults.length > 0 && (
                <span id="view-all-accounts" className="link" onClick={() => onAccountViewAllClick()}>
                  <span>{ t('view_all')}</span>
                  <i className="fas fa-chevron-right link-chevron"></i>
                </span>
              )}
            </div>
            {accountsLoading && <i className="fas fa-spinner fa-spin"></i>}
          </div>

          {accountsLoading && accountSearchResults.length === 0 && (
            <div className="message-item">{ t('global:please_wait')}</div>
          )}

          {!accountsLoading && accountSearchResults.length === 0 && accountPartnerSearchResults.length === 0 && (
            <div id="accounts-message" className="message-item">
              No matches.
            </div>
          )}

          <div id="account-search-results">
            {accountSearchResults.map((result: AccountInfo) => (
              <div
                id={slugify(result.name)}
                key={result.id}
                className={'search-item'}
                onClick={() => onAccountClick(result.id)}>
                <div className={result.active ? '' : 'inactive'}>{result.name}</div>
                <div className={'account-text ' + (result.subscriptionActive ? '' : 'inactive')}>
                  {result.subscriptionName}
                </div>
              </div>
            ))}
          </div>

          {renderAccountSearchLinks()}
        </div>
      )}
    </>
  )
}

export default NavSearch
